import {FC, useEffect, useState} from 'react'
import {api, ApiError, CleanerEarning, CleanerPayout, CleanerTab} from '../../services/api'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'

type PayoutInfo = {owed: string; owedCount: number; connectStatus: 'NONE' | 'PENDING' | 'VERIFIED'; availableCents: number | null; canPayStripe: boolean; autoPayoutEnabled: boolean}

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const money = (n: string | number): string => `$${parseFloat(String(n || '0')).toFixed(2)}`
const fmtDate = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, {dateStyle: 'medium'})
}

const Card: FC<{title: string; sub?: string; right?: React.ReactNode; children: React.ReactNode}> = ({title, sub, right, children}) => (
  <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', padding: '24px 26px', marginBottom: 18}}>
    <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16}}>
      <div>
        <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: 0}}>{title}</h2>
        {sub && <p style={{fontSize: 13.5, color: MUTED, margin: '4px 0 0'}}>{sub}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
)

const EARNING_BADGE: Record<CleanerEarning['status'], {label: string; bg: string; color: string}> = {
  OWED: {label: 'On the tab', bg: 'rgba(3,149,64,0.12)', color: '#1f9d5b'},
  PENDING_APPROVAL: {label: 'Pending', bg: 'rgba(255,168,0,0.15)', color: '#b8770a'},
  PAID: {label: 'Paid', bg: '#eef1f6', color: MUTED},
  DECLINED: {label: 'Declined', bg: '#fef2f2', color: '#dc2626'},
}

const smallBtn = (bg: string, color: string, disabled: boolean): React.CSSProperties => ({
  fontWeight: 700, fontSize: 13, color, background: bg, border: 'none', borderRadius: 8,
  padding: '7px 13px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  display: 'inline-flex', alignItems: 'center', gap: 6,
})

/** The cleaner's tab, pending-approval visits, and full ledger — admin view. */
const CleanerAdminLedger: FC = () => {
  const toast = useToast()
  const [tab, setTab] = useState<CleanerTab | null>(null)
  const [pending, setPending] = useState<CleanerEarning[]>([])
  const [ledger, setLedger] = useState<CleanerEarning[]>([])
  const [payouts, setPayouts] = useState<CleanerPayout[]>([])
  const [info, setInfo] = useState<PayoutInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [adjustId, setAdjustId] = useState<string | null>(null)
  const [adjustVal, setAdjustVal] = useState('')
  const [paying, setPaying] = useState<'stripe' | 'manual' | null>(null)
  const [ledgerPage, setLedgerPage] = useState(0)

  const load = () => {
    Promise.all([
      api.cleanerTab().catch(() => null),
      api.adminPendingCleanerEarnings().catch(() => []),
      api.adminCleanerEarnings().catch(() => []),
      api.adminCleanerPayouts().catch(() => []),
      api.cleanerPayoutInfo().catch(() => null),
    ]).then(([t, p, l, po, i]) => {
      setTab(t)
      setPending(p)
      setLedger(l)
      setPayouts(po)
      setInfo(i)
    }).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const payStripe = async () => {
    setPaying('stripe')
    try {
      const p = await api.stripeCleanerPayout()
      toast.success(`Paid ${money(p.amount)} to the cleaner. It’ll reach her bank in 1–2 business days.`)
      load()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Payout failed')
    } finally {
      setPaying(null)
    }
  }

  const payManual = async () => {
    setPaying('manual')
    try {
      const r = await api.manualCleanerPayout('Marked paid outside the app')
      if ('ok' in r && r.ok === false) toast.error(r.reason)
      else toast.success('Marked as paid. The tab is now settled.')
      load()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not mark paid')
    } finally {
      setPaying(null)
    }
  }

  const approve = async (e: CleanerEarning, amount?: string) => {
    setBusyId(e.id)
    try {
      await api.approveCleanerEarning(e.id, amount)
      toast.success(`Approved ${money(amount ?? e.amount)}.`)
      setAdjustId(null)
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not approve')
    } finally {
      setBusyId(null)
    }
  }

  const decline = async (e: CleanerEarning) => {
    setBusyId(e.id)
    try {
      await api.declineCleanerEarning(e.id)
      toast.success('Visit declined.')
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not decline')
    } finally {
      setBusyId(null)
    }
  }

  const reachedThreshold = tab && parseFloat(tab.owed) >= parseFloat(tab.threshold || '0') && parseFloat(tab.owed) > 0

  const LEDGER_PAGE = 10
  const ledgerPages = Math.max(1, Math.ceil(ledger.length / LEDGER_PAGE))
  const ledgerCur = Math.min(ledgerPage, ledgerPages - 1)
  const ledgerSlice = ledger.slice(ledgerCur * LEDGER_PAGE, ledgerCur * LEDGER_PAGE + LEDGER_PAGE)
  const pagerBtn = (disabled: boolean): React.CSSProperties => ({fontWeight: 700, fontSize: 13, color: disabled ? '#c2c6d2' : INK, background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', padding: '4px 8px'})

  const [threshEdit, setThreshEdit] = useState(false)
  const [threshVal, setThreshVal] = useState('')
  const [savingThresh, setSavingThresh] = useState(false)
  const saveThreshold = async () => {
    const n = parseFloat(threshVal)
    if (!Number.isFinite(n) || n < 0) return toast.error('Enter a valid amount.')
    setSavingThresh(true)
    try {
      await api.updatePaymentSettings({cleanerPayoutThreshold: n.toFixed(2)})
      setTab((t) => (t ? {...t, threshold: n.toFixed(2)} : t))
      setThreshEdit(false)
      toast.success(`Payout threshold set to ${money(n)}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update threshold')
    } finally {
      setSavingThresh(false)
    }
  }

  const [togglingAuto, setTogglingAuto] = useState(false)
  const toggleAuto = async () => {
    if (!info) return
    const next = !info.autoPayoutEnabled
    setTogglingAuto(true)
    try {
      await api.updatePaymentSettings({cleanerAutoPayoutEnabled: next})
      setInfo({...info, autoPayoutEnabled: next})
      toast.success(next
        ? `Auto-payout on — she’s paid automatically once her tab reaches ${money(tab?.threshold ?? '0')}.`
        : 'Auto-payout off — payouts are manual again.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update auto-payout')
    } finally {
      setTogglingAuto(false)
    }
  }

  return (
    <>
      {/* Running tab + payout */}
      <Card title='What you owe the cleaner' sub='Everything on the tab that hasn’t been paid out yet.'>
        {loading ? (
          <div style={{color: MUTED}}>Loading…</div>
        ) : (
          <>
            <div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'}}>
              <div>
                <div style={{fontSize: 34, fontWeight: 900, color: reachedThreshold ? ACCENT : INK, lineHeight: 1.1}}>{money(tab?.owed ?? '0')}</div>
                <div style={{fontSize: 13, color: MUTED, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                  <span>{tab?.owedCount ?? 0} item{(tab?.owedCount ?? 0) === 1 ? '' : 's'} · threshold</span>
                  {threshEdit ? (
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                      <span style={{fontWeight: 700, color: INK}}>$</span>
                      <input
                        value={threshVal}
                        onChange={(e) => setThreshVal(e.target.value)}
                        inputMode='decimal'
                        autoFocus
                        style={{width: 80, padding: '5px 8px', border: '1px solid #d7dbe6', borderRadius: 8, fontSize: 13}}
                      />
                      <button onClick={saveThreshold} disabled={savingThresh} style={{fontWeight: 700, fontSize: 12.5, color: '#fff', background: ACCENT, border: 'none', borderRadius: 8, padding: '5px 12px', cursor: savingThresh ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6}}>
                        {savingThresh && <Spinner size={12} />} Save
                      </button>
                      <button onClick={() => setThreshEdit(false)} style={{fontWeight: 700, fontSize: 12.5, color: MUTED, background: 'none', border: 'none', cursor: 'pointer'}}>Cancel</button>
                    </span>
                  ) : (
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                      <strong style={{color: INK}}>{money(tab?.threshold ?? '0')}</strong>
                      <button onClick={() => {setThreshVal(parseFloat(tab?.threshold ?? '0').toFixed(2)); setThreshEdit(true)}} style={{fontWeight: 700, fontSize: 12.5, color: ACCENT, background: 'none', border: 'none', cursor: 'pointer', padding: 0}}>Edit</button>
                    </span>
                  )}
                </div>
              </div>
              {reachedThreshold && (
                <span style={{fontSize: 12.5, fontWeight: 700, color: '#1f9d5b', background: 'rgba(3,149,64,0.12)', borderRadius: 999, padding: '6px 13px'}}>
                  Ready to pay out
                </span>
              )}
            </div>

            {parseFloat(tab?.owed ?? '0') > 0 && (
              <div style={{marginTop: 18, paddingTop: 18, borderTop: '1px solid #f1f3f7'}}>
                {info?.availableCents != null && (
                  <div style={{fontSize: 13, color: MUTED, marginBottom: 12}}>
                    Available Stripe balance: <strong style={{color: INK}}>{money(info.availableCents / 100)}</strong>
                    {info.connectStatus === 'VERIFIED' && !info.canPayStripe && (
                      <span style={{color: '#b8770a'}}> · not enough to cover the payout yet (card funds clear in ~2 days)</span>
                    )}
                  </div>
                )}
                <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                  {info?.connectStatus === 'VERIFIED' ? (
                    <button
                      onClick={payStripe}
                      disabled={paying !== null || !info?.canPayStripe}
                      style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 24px', cursor: paying || !info?.canPayStripe ? 'not-allowed' : 'pointer', opacity: paying || !info?.canPayStripe ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 8}}
                      title={!info?.canPayStripe ? 'Not enough available Stripe balance yet' : ''}
                    >
                      {paying === 'stripe' && <Spinner />} Pay the cleaner {money(tab?.owed ?? '0')} via Stripe
                    </button>
                  ) : (
                    <span style={{fontSize: 13, color: MUTED, alignSelf: 'center'}}>
                      Set up her Stripe payout below to pay with one click, or mark a manual payment.
                    </span>
                  )}
                  <button
                    onClick={payManual}
                    disabled={paying !== null}
                    style={{fontWeight: 700, fontSize: 15, color: INK, background: '#eef1f6', border: 'none', borderRadius: 10, padding: '12px 22px', cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 8}}
                  >
                    {paying === 'manual' && <Spinner color={INK} />} Mark as paid (Zelle/other)
                  </button>
                </div>
              </div>
            )}

            {info?.connectStatus === 'VERIFIED' && (
              <div style={{marginTop: 18, paddingTop: 18, borderTop: '1px solid #f1f3f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap'}}>
                <div style={{minWidth: 0, flex: 1}}>
                  <div style={{fontSize: 14, fontWeight: 700, color: INK}}>Automatic payouts</div>
                  <div style={{fontSize: 12.5, color: MUTED, marginTop: 2, maxWidth: 460}}>
                    When on, she’s paid via Stripe automatically once her tab reaches {money(tab?.threshold ?? '0')} and the balance covers it — no click needed.
                  </div>
                </div>
                <button
                  onClick={toggleAuto}
                  disabled={togglingAuto}
                  role='switch'
                  aria-checked={info.autoPayoutEnabled}
                  style={{position: 'relative', width: 48, height: 27, borderRadius: 999, border: 'none', flexShrink: 0, cursor: togglingAuto ? 'not-allowed' : 'pointer', background: info.autoPayoutEnabled ? ACCENT : '#cfd4de', transition: 'background .2s', opacity: togglingAuto ? 0.6 : 1}}
                >
                  <span style={{position: 'absolute', top: 3, left: info.autoPayoutEnabled ? 24 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left .2s'}} />
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <Card title='Pending your approval' sub='Cleaning visits logged above the base rate. Approve, adjust the amount, or decline.'>
          {pending.map((e) => {
            const busy = busyId === e.id
            const adjusting = adjustId === e.id
            return (
              <div key={e.id} style={{padding: '14px 0', borderTop: '1px solid #f1f3f7'}}>
                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'}}>
                  <div style={{minWidth: 0, flex: 1}}>
                    <div style={{fontSize: 15, fontWeight: 800, color: INK}}>{money(e.amount)} · Apartment cleaning</div>
                    <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>{fmtDate(e.occurredOn)}</div>
                    {e.note && <div style={{fontSize: 13.5, color: '#374151', marginTop: 6, background: '#f8fafc', border: '1px solid #eef0f4', borderRadius: 8, padding: '8px 12px'}}>“{e.note}”</div>}
                  </div>
                  <div style={{display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap'}}>
                    {!adjusting && (
                      <>
                        <button style={smallBtn(ACCENT, '#fff', busy)} disabled={busy} onClick={() => approve(e)}>{busy ? <Spinner size={12} /> : 'Approve'}</button>
                        <button style={smallBtn('#eef1f6', INK, busy)} disabled={busy} onClick={() => { setAdjustId(e.id); setAdjustVal(e.amount) }}>Adjust</button>
                        <button style={smallBtn('#fef2f2', '#dc2626', busy)} disabled={busy} onClick={() => decline(e)}>Decline</button>
                      </>
                    )}
                  </div>
                </div>
                {adjusting && (
                  <div style={{display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap'}}>
                    <span style={{fontSize: 13, color: MUTED}}>New amount $</span>
                    <input type='number' min='0' step='0.01' value={adjustVal} onChange={(ev) => setAdjustVal(ev.target.value)} style={{width: 110, padding: '8px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14}} />
                    <button style={smallBtn(ACCENT, '#fff', busy)} disabled={busy} onClick={() => approve(e, adjustVal)}>{busy ? <Spinner size={12} /> : 'Save & approve'}</button>
                    <button style={smallBtn('#eef1f6', INK, busy)} disabled={busy} onClick={() => setAdjustId(null)}>Cancel</button>
                  </div>
                )}
              </div>
            )
          })}
        </Card>
      )}

      {/* Full ledger */}
      <Card title='Earnings ledger' sub='Every bed wash and cleaning visit, newest first.'>
        {loading ? (
          <div style={{color: MUTED}}>Loading…</div>
        ) : ledger.length === 0 ? (
          <div style={{color: MUTED, padding: '8px 0'}}>No earnings yet.</div>
        ) : (
          <>
            {ledgerSlice.map((e) => {
              const b = EARNING_BADGE[e.status]
              return (
                <div key={e.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderTop: '1px solid #f1f3f7'}}>
                  <div style={{minWidth: 0}}>
                    <div style={{fontSize: 14.5, fontWeight: 700, color: INK}}>
                      {e.kind === 'BED_WASH' ? `Bed wash${e.bedCode ? ` — Bed ${e.bedCode}` : ''}` : 'Apartment cleaning'}
                    </div>
                    <div style={{fontSize: 12.5, color: MUTED, marginTop: 2}}>{fmtDate(e.occurredOn)}{e.note ? ` · ${e.note}` : ''}</div>
                  </div>
                  <div style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                    <div style={{fontSize: 15, fontWeight: 800, color: INK}}>{money(e.amount)}</div>
                    <span style={{display: 'inline-block', marginTop: 3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', borderRadius: 999, padding: '2px 8px', background: b.bg, color: b.color}}>{b.label}</span>
                  </div>
                </div>
              )
            })}
            {ledgerPages > 1 && (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, marginTop: 4, borderTop: '1px solid #f1f3f7'}}>
                <button disabled={ledgerCur === 0} onClick={() => setLedgerPage(ledgerCur - 1)} style={pagerBtn(ledgerCur === 0)}>‹ Prev</button>
                <span style={{fontSize: 13, color: MUTED}}>Page {ledgerCur + 1} of {ledgerPages}</span>
                <button disabled={ledgerCur >= ledgerPages - 1} onClick={() => setLedgerPage(ledgerCur + 1)} style={pagerBtn(ledgerCur >= ledgerPages - 1)}>Next ›</button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Payout history */}
      {payouts.length > 0 && (
        <Card title='Payout history' sub='Money already paid out to the cleaner.'>
          {payouts.map((p) => (
            <div key={p.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderTop: '1px solid #f1f3f7'}}>
              <div>
                <div style={{fontSize: 14.5, fontWeight: 700, color: INK}}>{money(p.amount)}</div>
                <div style={{fontSize: 12.5, color: MUTED, marginTop: 2}}>{p.method === 'STRIPE' ? 'Stripe transfer' : 'Marked paid'} · {fmtDate(p.paidAt ?? p.createdAt)}{p.note ? ` · ${p.note}` : ''}</div>
              </div>
              <span style={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderRadius: 999, padding: '3px 9px', color: '#1f9d5b', background: 'rgba(3,149,64,0.12)'}}>Paid</span>
            </div>
          ))}
        </Card>
      )}
    </>
  )
}

export {CleanerAdminLedger}
